import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Video, PhoneOff, Plus, Loader2, Users, Clock, Trash2, ExternalLink, CalendarPlus, Play, Landmark, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useMeetingSession } from "../lib/meetingSession";
import { fetchObedienceType, pluralKey } from "../lib/gender";

// Hard-coded fallback Jitsi domain. Old meetings stored with calyx /
// riot.im / etc. are forcibly rewritten to this host on the client so a
// dead/cached domain never strands a brother on a black screen.
// meet.jit.si now requires a signed-in moderator to start conferences —
// meet.ffmuc.net (Freifunk München) still allows anonymous rooms. Keep in
// sync with JITSI_CANONICAL_HOST in backend/server.py.
const JITSI_HOST = "meet.ffmuc.net";

/**
 * Load the official Jitsi Meet External API library on demand. Returns
 * a Promise that resolves to `window.JitsiMeetExternalAPI`.
 *
 * The official library is the only reliable way to embed a Jitsi room on
 * iOS Safari — a raw <iframe> trips Apple's universal-link redirect to
 * the native Jitsi Meet app, which fails with "Safari can't open this
 * page" if the app isn't installed. The external API explicitly opts
 * out of deep linking when configured with `disableDeepLinking: true`.
 */
function loadJitsiExternalApi() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.JitsiMeetExternalAPI) return Promise.resolve(window.JitsiMeetExternalAPI);
  // Cache the in-flight loader so concurrent mounts share one <script>.
  if (window.__jitsiExternalApiLoader) return window.__jitsiExternalApiLoader;
  window.__jitsiExternalApiLoader = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://${JITSI_HOST}/external_api.js`;
    script.async = true;
    script.onload = () => {
      if (window.JitsiMeetExternalAPI) resolve(window.JitsiMeetExternalAPI);
      else reject(new Error("Jitsi External API failed to attach"));
    };
    script.onerror = () => reject(new Error("Jitsi External API script load error"));
    document.head.appendChild(script);
  });
  return window.__jitsiExternalApiLoader;
}

/**
 * Online Meetings page (Web Admin).
 *
 * Worshipful Master / lodge admins start a Jitsi Meet session for the lodge.
 * The Jitsi room is embedded directly via iframe (meet.jit.si) and the
 * meeting URL is broadcast to all lodge members through their mobile
 * notifications inbox (see backend `/meetings`). Only ONE meeting is
 * "live" per lodge at any time.
 */
export default function OnlineMeetingsPage() {
  const { t, i18n } = useTranslation();
  const locale =t("onlineMeetings.en_gb");
  const { user } = useAuth();
  // Only the parent-obedience admin may address a meeting to child lodges.
  const isObedienceAdmin = !!user && user.role === "admin" && !user.child_lodge_id;
  const [meetings, setMeetings] = useState([]);
  const [active, setActive] = useState(null);
  const [attendees, setAttendees] = useState([]);

  useEffect(() => {
    if (!active?.id) { setAttendees([]); return; }
    let stop = false;
    const loadAttendees = () =>
      api.get(`/meetings/${active.id}/participants`)
        .then(({ data }) => { if (!stop) setAttendees(data.participants || []); })
        .catch(() => {});
    loadAttendees();
    const iv = setInterval(loadAttendees, 15000);
    return () => { stop = true; clearInterval(iv); };
  }, [active?.id]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [agenda, setAgenda] = useState("");
  const [audience, setAudience] = useState("members");
  const [degree, setDegree] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [obedienceType, setObedienceType] = useState("regulara");
  useEffect(() => { fetchObedienceType().then(setObedienceType); }, []);
  const brothersLabel = t(pluralKey("meetingsPage.brothers", obedienceType), { defaultValue: t("meetingsPage.brothers", "frați") });

  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const resetForm = () => {
    setTitle(""); setDescription(""); setAgenda("");
    setAudience("members"); setDegree(""); setScheduledAt("");
    setEditingId(null); setShowForm(false);
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setTitle(m.title || "");
    setDescription(m.description || "");
    setAgenda(m.agenda || "");
    setAudience(m.audience || "members");
    setDegree(m.grand_only ? "grand" : (m.degree ? String(m.degree) : ""));
    setScheduledAt(toLocalInput(m.scheduled_at));
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/meetings/active"),
        api.get("/meetings"),
      ]);
      setActive(a.data || null);
      setMeetings(b.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000); // light polling
    return () => clearInterval(interval);
  }, []);

  const startMeeting = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) return;
    setCreating(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        agenda: agenda.trim() || null,
        audience,
        degree: audience === "members" && degree ? (degree === "grand" ? "grand" : Number(degree)) : null,
      };
      if (scheduledAt) {
        payload.scheduled_at = new Date(scheduledAt).toISOString();
        payload.scheduled_local = new Date(scheduledAt).toLocaleString(locale, {
          day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
      }
      if (editingId) {
        await api.put(`/meetings/${editingId}`, payload);
        toast.success(t("meetingsPage.updatedOk", "Ședința a fost actualizată."));
        resetForm();
        await load();
        return;
      }
      const { data } = await api.post("/meetings", payload);
      if (data.status === "live") {
        setActive(data);
        toast.success(t("onlineMeetings.meeting_started"));
      } else {
        toast.success(t("meetingsPage.scheduledOk", "Ședință programată — participanții au fost notificați."));
      }
      setMeetings((prev) => [data, ...prev]);
      setTitle("");
      setDescription("");
      setAudience("members");
      setDegree("");
      setScheduledAt("");
      setShowForm(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const startScheduled = async (m) => {
    try {
      const { data } = await api.post(`/meetings/${m.id}/start`);
      setActive(data);
      toast.success(t("onlineMeetings.meeting_started"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadIcs = async (m) => {
    try {
      const res = await api.get(`/meetings/${m.id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `meeting-${m.id.slice(0, 8)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const endMeeting = async () => {
    if (!active) return;
    if (!window.confirm(t("onlineMeetings.end_the_meeting_for_everyone"))) return;
    try {
      await api.post(`/meetings/${active.id}/end`);
      toast.success(t("onlineMeetings.meeting_ended"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm(t("onlineMeetings.delete_this_meeting_record"))) return;
    try {
      await api.delete(`/meetings/${id}`);
      setMeetings((prev) => prev.filter((m) => m.id !== id));
      if (active?.id === id) setActive(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const scheduled = useMemo(
    () => meetings.filter((m) => m.status === "scheduled"),
    [meetings],
  );
  const past = useMemo(
    () => meetings.filter((m) => m.status === "ended").slice(0, 20),
    [meetings],
  );

  const containerRef = useRef(null);   // div mount point for the External API
  const apiRef = useRef(null);         // active JitsiMeetExternalAPI instance
  const [embedError, setEmbedError] = useState("");
  const [embedReady, setEmbedReady] = useState(false);

  // ---- LiveKit (preferred since 2026-06-11) ----
  // 'loading' → fetching token; {url, token} → render LiveKit room;
  // 'unavailable' → backend has no LiveKit credentials → Jitsi fallback.
  const [lk, setLk] = useState("loading");
  const { session, startSession, endSession, setHost } = useMeetingSession();
  const hostRef = useCallback((el) => setHost(el), [setHost]);
  useEffect(() => () => setHost(null), [setHost]);
  useEffect(() => {
    let cancelled = false;
    if (!active) { setLk("loading"); if (session) endSession(); return () => { cancelled = true; }; }
    if (session?.meetingId === active.id) { setLk({ url: session.url, token: session.token }); return () => { cancelled = true; }; }
    setLk("loading");
    api.post(`/meetings/${active.id}/livekit-token`)
      .then(({ data }) => {
        if (cancelled) return;
        setLk(data?.token ? data : "unavailable");
        if (data?.token) {
          startSession({
            meetingId: active.id, title: active.title, url: data.url, token: data.token,
            canRecord: !user?.child_lodge_id || active.child_lodge_id === user?.child_lodge_id,
          });
        }
      })
      .catch(() => { if (!cancelled) setLk("unavailable"); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);
  const rejoin = () => {
    if (!active || typeof lk !== "object") return;
    startSession({
      meetingId: active.id, title: active.title, url: lk.url, token: lk.token,
      canRecord: !user?.child_lodge_id || active.child_lodge_id === user?.child_lodge_id,
    });
  };

  // Whenever the active meeting changes (start / end / switch), tear
  // down the previous Jitsi instance and mount a new one. Runs ONLY as a
  // FALLBACK when LiveKit credentials are missing on the backend.
  useEffect(() => {
    let cancelled = false;
    const dispose = () => {
      try { apiRef.current?.dispose?.(); } catch { /* noop */ }
      apiRef.current = null;
    };

    if (!active || lk !== "unavailable") {
      dispose();
      setEmbedReady(false);
      setEmbedError("");
      return () => { cancelled = true; };
    }

    setEmbedReady(false);
    setEmbedError("");

    loadJitsiExternalApi()
      .then((JitsiMeetExternalAPI) => {
        if (cancelled || !containerRef.current) return;
        dispose();
        const options = {
          roomName: active.room_name,
          parentNode: containerRef.current,
          width: "100%",
          height: "100%",
          configOverwrite: {
            // Anti-deep-link → keeps iOS Safari on the web client instead
            // of bouncing through the native-app redirect.
            disableDeepLinking: true,
            prejoinPageEnabled: false,
            // NEW key — `prejoinPageEnabled` is deprecated on upgraded
            // Jitsi instances (ffmuc) and silently ignored.
            prejoinConfig: { enabled: false },
            // Soft-disable the lobby so the brother joins straight away.
            enableLobbyChat: false,
            requireDisplayName: false,
            // Defeat the "5-minute solo kick" timer.
            disableInactivityCheck: true,
            disableInactivityNotifications: true,
            inactivityTimeoutMs: 0,
            // Prevent iOS Safari throttling the video when the tab loses focus.
            disableSuspendVideo: true,
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            subject: active.title || "Lodge meeting",
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            MOBILE_APP_PROMO: false,
            SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            HIDE_INVITE_MORE_HEADER: true,
          },
          userInfo: {
            displayName: active.started_by || "Brother",
          },
        };
        const api2 = new JitsiMeetExternalAPI(JITSI_HOST, options);
        apiRef.current = api2;
        api2.addListener("videoConferenceJoined", () => setEmbedReady(true));
        api2.addListener("readyToClose", () => endMeeting());
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Jitsi External API load failed", err);
        setEmbedError(t("onlineMeetings.could_not_load_the_meeting"));
      });

    return () => { cancelled = true; dispose(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, active?.room_name, lk]);

  // Force-rewrite the meeting URL to the canonical host so an obsolete
  // domain stored in DB (e.g. calyx, riot.im) never produces a dead link.
  const safeMeetingUrl = useMemo(() => {
    if (!active) return null;
    return `https://${JITSI_HOST}/${active.room_name}#config.disableDeepLinking=true`;
  }, [active]);

  return (
    <div className="page-pad" data-testid="online-meetings-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Video size={22} className="text-gold-500" />
            {t("meetingsPage.pageTitle", "Întâlniri online")}
          </h1>
          <p className="page-subtitle">
            {t("meetingsPage.pageSubtitle", "Pornește o ședință live securizată — frații sunt notificați automat în aplicația mobilă.")}
          </p>
        </div>
        {!active && !showForm ? (
          <button
            type="button"
            className="btn-primary-gold flex items-center gap-2"
            onClick={() => setShowForm(true)}
            data-testid="btn-start-meeting"
          >
            <Plus size={16} /> {t("meetingsPage.startNew", "ÎNCEPE ȘEDINȚA")}
          </button>
        ) : null}
      </div>

      {/* Compose form */}
      {showForm && !active && (
        <div className="gold-card mb-4" style={{ padding: "1.2rem" }}>
          <form onSubmit={startMeeting} className="space-y-3">
            <div>
              <label className="kv-label block mb-1">{t("meetingsPage.title", "Titlu ședință")}</label>
              <input
                type="text"
                className="input-lodge w-full"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("onlineMeetings.e_g_monthly_lodge_meeting")}
                autoFocus
                data-testid="input-meeting-title"
              />
            </div>
            <div>
              <label className="kv-label block mb-1">{t("meetingsPage.description", "Descriere (opțional)")}</label>
              <textarea
                className="input-lodge w-full"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                data-testid="input-meeting-description"
              />
            </div>
            <div>
              <label className="kv-label block mb-1">{t("meetingsPage.agenda", "Ordinea de zi (opțional)")}</label>
              <textarea
                className="input-lodge w-full"
                value={agenda}
                onChange={(e) => setAgenda(e.target.value)}
                rows={4}
                placeholder={t("meetingsPage.agendaPlaceholder", "1. Deschiderea lucrărilor…")}
                data-testid="input-meeting-agenda"
              />
            </div>
            {isObedienceAdmin && !editingId && (
              <div>
                <label className="kv-label block mb-1">{t("meetingsPage.audience", "Tipul ședinței")}</label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className={audience === "members" ? "btn-primary-gold flex items-center gap-2" : "btn-outline-gold flex items-center gap-2"}
                    onClick={() => setAudience("members")}
                    data-testid="audience-members"
                  >
                    <Users size={14} /> {t("meetingsPage.audienceMembers", "Pentru membri")}
                  </button>
                  <button
                    type="button"
                    className={audience === "lodges" ? "btn-primary-gold flex items-center gap-2" : "btn-outline-gold flex items-center gap-2"}
                    onClick={() => setAudience("lodges")}
                    data-testid="audience-lodges"
                  >
                    <Landmark size={14} /> {t("meetingsPage.audienceLodges", "Pentru loji")}
                  </button>
                </div>
                {audience === "lodges" && (
                  <p className="kv-label" style={{ fontSize: "0.68rem", marginTop: 4 }}>
                    {t("meetingsPage.audienceLodgesHint", "Doar lojile din jurisdicția ta pot intra — exclusiv de pe aplicația web. Membrii nu văd această ședință.")}
                  </p>
                )}
              </div>
            )}
            {audience === "members" && (
              <div>
                <label className="kv-label block mb-1">{t("meetingsPage.degree", "Gradul ședinței")}</label>
                <select
                  className="input-lodge w-full"
                  value={degree}
                  onChange={(e) => setDegree(e.target.value)}
                  data-testid="input-meeting-degree"
                >
                  <option value="">{t("meetingsPage.degreeAll", "— toți membrii —")}</option>
                  <option value="grand">
                    {user?.child_lodge_id
                      ? t("meetingsPage.lodgeOfficers", "Ofițerii Lojii")
                      : t("meetingsPage.grandOfficers", "Marii Ofițeri")}
                  </option>
                  {Array.from({ length: 33 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>{t("meetingsPage.degreeOpt", { deg: d, defaultValue: `Grad ${d}+` })}</option>
                  ))}
                </select>
                <p className="kv-label" style={{ fontSize: "0.68rem", marginTop: 4 }}>
                  {t("meetingsPage.degreeHint", "Doar membrii cu gradul selectat sau mai mare sunt notificați și pot intra.")}
                </p>
              </div>
            )}
            <div>
              <label className="kv-label block mb-1">{t("meetingsPage.scheduleAt", "Programează (opțional — gol = pornește acum)")}</label>
              <input
                type="datetime-local"
                className="input-lodge w-full"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="input-meeting-schedule"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-outline-gold" onClick={resetForm} data-testid="btn-cancel-meeting">
                {t("common.cancel", "Anulează")}
              </button>
              <button type="submit" className="btn-primary-gold flex items-center gap-2" disabled={creating || !title.trim()} data-testid="btn-create-meeting">
                {creating ? <Loader2 size={14} className="animate-spin" /> : editingId ? <Pencil size={14} /> : <Video size={14} />}
                {creating
                  ? t("common.loading", "Se încarcă...")
                  : editingId
                    ? t("meetingsPage.saveChanges", "SALVEAZĂ MODIFICĂRILE")
                    : scheduledAt
                      ? t("meetingsPage.schedule", "PROGRAMEAZĂ")
                      : t("meetingsPage.go", "PORNEȘTE")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Active meeting embed */}
      {active && (
        <div className="gold-card" style={{ padding: "1.2rem", marginBottom: "1.2rem" }} data-testid="active-meeting-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="kv-label" style={{ color: "#3e6a3c", letterSpacing: "0.2em" }}>
                ● {t("meetingsPage.live", "LIVE")}
                {active.audience === "lodges" ? (
                  <span style={{ marginLeft: 8, color: "#7c5c14" }}>{t("meetingsPage.badgeLodges", "PENTRU LOJI")}</span>
                ) : active.degree ? (
                  <span style={{ marginLeft: 8, color: "#7c5c14" }}>{t("meetingsPage.badgeDegree", { deg: active.degree, defaultValue: `GRAD ${active.degree}+` })}</span>
                ) : null}
              </div>
              <h2 style={{ fontFamily: "Cinzel, serif", fontSize: "1.3rem", color: "#0d1b2a", margin: "0.2rem 0" }} data-testid="active-meeting-title">
                {active.title}
              </h2>
              {active.description && <p style={{ color: "#34322d", fontSize: "0.9rem" }}>{active.description}</p>}
              {active.agenda && (
                <div style={{ margin: "0.4rem 0", padding: "0.6rem 0.8rem", background: "#f6f1e2", border: "1px solid #c9a84c", borderRadius: 6 }} data-testid="active-meeting-agenda">
                  <div style={{ fontFamily: "Cinzel, serif", fontSize: "0.72rem", letterSpacing: "0.14em", color: "#7a6428", marginBottom: 4 }}>
                    {t("meetingsPage.agendaLabel", "ORDINEA DE ZI")}
                  </div>
                  <p style={{ color: "#34322d", fontSize: "0.88rem", whiteSpace: "pre-wrap", margin: 0 }}>{active.agenda}</p>
                </div>
              )}
              {attendees.length > 0 && (
                <div style={{ margin: "0.4rem 0" }} data-testid="meeting-participants">
                  <div style={{ fontFamily: "Cinzel, serif", fontSize: "0.72rem", letterSpacing: "0.14em", color: "#7a6428", marginBottom: 4 }}>
                    {t("meetingsPage.participants", "Participanți")} ({attendees.length})
                  </div>
                  <p style={{ color: "#34322d", fontSize: "0.85rem", margin: 0 }}>
                    {attendees.map((p) => p.name).filter(Boolean).join(", ")}
                  </p>
                </div>
              )}
              <div className="kv-label" style={{ marginTop: "0.4rem", fontSize: "0.7rem" }}>
                <Clock size={11} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
                {new Date(active.started_at).toLocaleString(locale, { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
                {" · "} {active.started_by}
                {active.participants?.length ? (
                  <>
                    {" · "} <Users size={11} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
                    {active.participants.length} {brothersLabel}
                  </>
                ) : null}
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <button type="button" className="btn-outline-gold flex items-center gap-2" onClick={() => downloadIcs(active)} data-testid="btn-ics-active" title={t("meetingsPage.addToCalendar", "Adaugă în calendar")}>
                <CalendarPlus size={14} />
              </button>
              {(!user?.child_lodge_id || active.child_lodge_id === user?.child_lodge_id) && (
                <button type="button" className="btn-outline-gold flex items-center gap-2" style={{ borderColor: "#7c2a2a", color: "#7c2a2a" }} onClick={endMeeting} data-testid="btn-end-meeting">
                  <PhoneOff size={14} /> {t("meetingsPage.end", "ÎNCHEIE")}
                </button>
              )}
            </div>
          </div>
          <div style={{ position: "relative", borderRadius: 6, border: "1px solid #c9a84c", background: "#0d1b2a", overflow: "hidden" }}>
            {typeof lk === "object" && lk?.token ? (
              <div style={{ height: "70vh", minHeight: 420, position: "relative" }} data-testid="livekit-container" data-lk-theme="default">
                {session?.meetingId === active.id ? (
                  <div ref={hostRef} style={{ height: "100%" }} data-testid="livekit-stage-host" />
                ) : (
                  <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: "#e8d59a" }} data-testid="livekit-left">
                    <span style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.14em", fontSize: "0.8rem" }}>{t("meetingsPage.youLeft", "Ai părăsit ședința — ea continuă pentru ceilalți.")}</span>
                    <button type="button" className="btn-primary-gold flex items-center gap-2" onClick={rejoin} data-testid="livekit-rejoin">
                      <Video size={14} /> {t("meetingsPage.rejoin", "REINTRĂ")}
                    </button>
                  </div>
                )}
              </div>
            ) : lk === "loading" ? (
              <div
                style={{
                  height: "40vh", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  flexDirection: "column", gap: 10,
                  color: "#e8d59a", fontFamily: "Cinzel, serif", letterSpacing: "0.16em",
                }}
                data-testid="livekit-loading"
              >
                <Loader2 size={24} className="animate-spin" />
                <span style={{ fontSize: "0.78rem" }}>{t("onlineMeetings.connecting_to_jitsi")}</span>
              </div>
            ) : (
              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
                <div
                  ref={containerRef}
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                  data-testid="jitsi-container"
                />
                {!embedReady && !embedError && (
                  <div
                    style={{
                      position: "absolute", inset: 0, display: "flex",
                      alignItems: "center", justifyContent: "center",
                      flexDirection: "column", gap: 10,
                      color: "#e8d59a", fontFamily: "Cinzel, serif", letterSpacing: "0.16em",
                      pointerEvents: "none",
                    }}
                    data-testid="jitsi-loading"
                  >
                    <Loader2 size={24} className="animate-spin" />
                    <span style={{ fontSize: "0.78rem" }}>
                      {t("onlineMeetings.connecting_to_jitsi")}
                    </span>
                  </div>
                )}
              </div>
            )}
            {embedError && (
              <div
                style={{
                  position: "absolute", inset: 0, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  textAlign: "center", padding: "1rem",
                  color: "#ffd9d9", fontFamily: "Crimson Text, serif",
                }}
                data-testid="jitsi-error"
              >
                {embedError}
              </div>
            )}
          </div>
          {/* Anti-5min-limit CTA banner — relevant ONLY for the Jitsi
              fallback. Hidden when the meeting runs on LiveKit. */}
          {lk === "unavailable" && (
          <div
            data-testid="meeting-fullscreen-banner"
            style={{
              marginTop: "0.8rem",
              padding: "0.8rem 1rem",
              background: "rgba(154, 117, 48, 0.10)",
              border: "1px solid rgba(154, 117, 48, 0.4)",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontFamily: "Cinzel, serif", fontSize: "0.78rem", letterSpacing: "0.16em", color: "#9a7530", marginBottom: 4 }}>
                {t("onlineMeetings.extend_beyond_5_minutes")}
              </div>
              <div style={{ fontFamily: "Crimson Text, serif", fontSize: "0.85rem", color: "#34322d" }}>
                {t("onlineMeetings.the_embedded_preview_is_limited")}
              </div>
            </div>
            <a
              href={safeMeetingUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-primary-gold flex items-center gap-2"
              style={{ textDecoration: "none", whiteSpace: "nowrap" }}
              data-testid="meeting-open-tab-btn"
            >
              <ExternalLink size={14} />{" "}
              {t("onlineMeetings.open_in_new_tab")}
            </a>
          </div>
          )}

          <div className="kv-label" style={{ marginTop: "0.6rem", fontSize: "0.7rem", textAlign: "center" }}>
            {t("onlineMeetings.tip_open_this_in_a")}
          </div>
        </div>
      )}

      {/* Empty / loading */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#9a7530" }}>
          <Loader2 className="animate-spin inline" size={20} />
        </div>
      ) : !active && !showForm ? (
        <div className="gold-card" style={{ padding: "2rem", textAlign: "center", marginBottom: "1.2rem" }}>
          <Video size={48} color="#c9a84c" style={{ opacity: 0.5, display: "block", margin: "0 auto 0.8rem" }} />
          <p style={{ color: "#34322d", fontSize: "0.95rem", marginBottom: "0.4rem" }}>
            {t("meetingsPage.noActive", "Nu există nicio întâlnire activă.")}
          </p>
          <p className="kv-label" style={{ fontSize: "0.7rem" }}>
            {t("meetingsPage.noActiveSub", "Apasă „ÎNCEPE ȘEDINȚA” pentru a porni o ședință live.")}
          </p>
        </div>
      ) : null}

      {/* Scheduled meetings */}
      {scheduled.length > 0 && (
        <div style={{ marginBottom: "1.2rem" }}>
          <h3 style={{ fontFamily: "Cinzel, serif", fontSize: "0.95rem", letterSpacing: "0.16em", color: "#9a7530", marginBottom: "0.6rem" }}>
            {t("meetingsPage.scheduledList", "ȘEDINȚE PROGRAMATE")}
          </h3>
          <div className="gold-card" style={{ padding: 0 }}>
            {scheduled.map((m, idx) => (
              <div
                key={m.id}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 8, flexWrap: "wrap",
                  padding: "0.75rem 1rem",
                  borderTop: idx > 0 ? "1px solid rgba(201,168,76,0.2)" : "none",
                }}
                data-testid={`scheduled-meeting-${m.id}`}
              >
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.95rem" }}>
                    {m.title}
                    {m.audience === "lodges" ? (
                      <span className="kv-label" style={{ marginLeft: 8, fontSize: "0.62rem", color: "#7c5c14" }}>
                        {t("meetingsPage.badgeLodges", "PENTRU LOJI")}
                      </span>
                    ) : m.degree ? (
                      <span className="kv-label" style={{ marginLeft: 8, fontSize: "0.62rem", color: "#7c5c14" }}>
                        {t("meetingsPage.badgeDegree", { deg: m.degree, defaultValue: `GRAD ${m.degree}+` })}
                      </span>
                    ) : null}
                  </div>
                  <div className="kv-label" style={{ fontSize: "0.66rem" }}>
                    <Clock size={11} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
                    {m.scheduled_at ? new Date(m.scheduled_at).toLocaleString(locale, { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    {" · "} {m.started_by}
                  </div>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <button
                    type="button"
                    className="btn-outline-gold flex items-center gap-2"
                    onClick={() => downloadIcs(m)}
                    data-testid={`btn-ics-${m.id}`}
                    title={t("meetingsPage.addToCalendar", "Adaugă în calendar")}
                  >
                    <CalendarPlus size={14} /> {t("meetingsPage.addToCalendar", "Adaugă în calendar")}
                  </button>
                  {(!user?.child_lodge_id || m.child_lodge_id === user?.child_lodge_id) && (
                    <>
                      <button
                        type="button"
                        className="btn-outline-gold flex items-center gap-2"
                        onClick={() => startEdit(m)}
                        data-testid={`btn-edit-scheduled-${m.id}`}
                        title={t("meetingsPage.edit", "Modifică")}
                      >
                        <Pencil size={14} /> {t("meetingsPage.edit", "Modifică")}
                      </button>
                      <button
                        type="button"
                        className="btn-primary-gold flex items-center gap-2"
                        onClick={() => startScheduled(m)}
                        disabled={!!active}
                        data-testid={`btn-start-scheduled-${m.id}`}
                      >
                        <Play size={14} /> {t("meetingsPage.startNow", "PORNEȘTE")}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteMeeting(m.id)}
                    style={{ background: "transparent", border: "none", color: "#7c2a2a", cursor: "pointer", padding: 6 }}
                    data-testid={`btn-delete-scheduled-${m.id}`}
                    title={t("common.delete", "Șterge")}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past meetings list */}
      {past.length > 0 && (
        <div>
          <h3 style={{ fontFamily: "Cinzel, serif", fontSize: "0.95rem", letterSpacing: "0.16em", color: "#9a7530", marginBottom: "0.6rem" }}>
            {t("meetingsPage.history", "ISTORIC ȘEDINȚE")}
          </h3>
          <div className="gold-card" style={{ padding: 0 }}>
            {past.map((m, idx) => (
              <div
                key={m.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.75rem 1rem",
                  borderTop: idx > 0 ? "1px solid rgba(201,168,76,0.2)" : "none",
                }}
                data-testid={`past-meeting-${m.id}`}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.95rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.title}
                  </div>
                  <div className="kv-label" style={{ fontSize: "0.66rem" }}>
                    {new Date(m.started_at).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    {m.ended_at ? ` → ${new Date(m.ended_at).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}` : ""}
                    {" · "} {m.started_by}
                    {m.participants?.length ? ` · ${m.participants.length} ${brothersLabel}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => deleteMeeting(m.id)}
                  style={{ background: "transparent", border: "none", color: "#7c2a2a", cursor: "pointer", padding: 6 }}
                  data-testid={`btn-delete-meeting-${m.id}`}
                  title={t("common.delete", "Șterge")}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
